import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronUp, ListMusic, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const errText = (d) => (typeof d === "string" ? d : "Something went wrong");

const RequestRow = ({ r, rank, onVote }) => (
  <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5" data-testid={`song-request-${r.id}`}>
    <button
      onClick={() => onVote(r)}
      className={`flex flex-col items-center justify-center w-12 h-12 rounded-lg border transition-colors shrink-0 ${
        r.voted ? "border-[#00f0ff]/50 bg-[#00f0ff]/10 text-[#00f0ff]" : "border-white/15 text-slate-400 hover:border-[#00f0ff]/40 hover:text-[#00f0ff]"
      }`}
      title={r.voted ? "Remove your vote" : "Vote for this song"}
      data-testid={`song-request-vote-${r.id}`}
    >
      <ChevronUp className="w-4 h-4" />
      <span className="font-tab text-xs leading-none" data-testid={`song-request-votes-${r.id}`}>{r.votes}</span>
    </button>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold truncate"><span className="text-slate-600 font-tab mr-2">#{rank}</span>{r.title}
        {r.reviewed && <span className="ml-2 text-[9px] font-tab uppercase tracking-widest rounded-full bg-emerald-400/15 text-emerald-400 px-1.5 py-0.5 align-middle" data-testid={`song-request-reviewed-${r.id}`}>Reviewed</span>}
      </div>
      {r.artist && <div className="text-xs text-slate-500 truncate">{r.artist}</div>}
    </div>
    <a
      href={`/?q=${encodeURIComponent(`${r.title}${r.artist ? ` ${r.artist}` : ""}`)}`}
      className="shrink-0 inline-flex items-center gap-1 text-xs font-tab text-[#ffab00] hover:underline"
      data-testid={`song-request-try-${r.id}`}
    >
      <Search className="w-3.5 h-3.5" /> Try it
    </a>
  </div>
);

export const SongRequests = ({ onRequireAuth }) => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const load = () => api.get("/song-requests").then((r) => setList(r.data)).catch(() => setList([]));
    load();
    window.addEventListener("beatflow:requests-changed", load);
    return () => window.removeEventListener("beatflow:requests-changed", load);
  }, [user]);

  const upsert = (item) => setList((l) => {
    const next = l.some((x) => x.id === item.id) ? l.map((x) => (x.id === item.id ? item : x)) : [...l, item];
    return next.sort((a, b) => b.votes - a.votes);
  });

  const vote = async (r) => {
    if (!user) return onRequireAuth();
    try {
      const { data } = await api.post(`/song-requests/${r.id}/vote`);
      upsert(data);
    } catch (e) {
      toast.error(errText(e.response?.data?.detail));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return onRequireAuth();
    if (!title.trim()) return;
    const norm = (s) => (s || "").trim().toLowerCase();
    const dup = list.find((x) => norm(x.title) === norm(title) && norm(x.artist) === norm(artist));
    if (dup?.voted) {
      toast.message("You've already voted for this one.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/song-requests", { title: title.trim(), artist: artist.trim() });
      upsert(data);
      setTitle(""); setArtist("");
      toast.success(dup ? "Vote added — thanks!" : "Request added — thanks for voting!");
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-12" data-testid="song-requests">
      <div className="flex items-center gap-3 mb-2">
        <ListMusic className="w-5 h-5 text-[#00f0ff]" />
        <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight">Song requests</h2>
      </div>
      <p className="text-sm text-slate-400 mb-6 max-w-xl">Which songs should we polish next? Add a song or vote for the ones you want — the most popular get checked and refined first.</p>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 mb-6 max-w-3xl">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Song title" className="bg-[#12161f] border-white/10" data-testid="song-request-title-input" />
        <Input value={artist} onChange={(e) => setArtist(e.target.value)} maxLength={120} placeholder="Artist (optional)" className="bg-[#12161f] border-white/10" data-testid="song-request-artist-input" />
        <Button type="submit" disabled={busy || !title.trim()} className="bg-[#00f0ff] text-black hover:bg-[#33f4ff] font-semibold shrink-0" data-testid="song-request-submit">
          <Plus className="w-4 h-4 mr-1" /> Request
        </Button>
      </form>
      {list.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="song-requests-empty">No requests yet — be the first!</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {list.slice(0, 20).map((r, i) => <RequestRow key={r.id} r={r} rank={i + 1} onVote={vote} />)}
        </div>
      )}
    </section>
  );
};
